import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlaceIcon from '@mui/icons-material/Place';
import api from '../api/client';
import OverviewTab from '../components/OverviewTab';
import ExpensesTab from '../components/ExpensesTab';
import WorkersTab from '../components/WorkersTab';

/**
 * Project detail with role-aware tabs:
 *   owner  → Overview | Expenses | Workers
 *   client → Overview | Expenses
 *   worker → Workers (their own ledger row)
 */
const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [summary, setSummary] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);

  const access = summary?.project?.access;

  const load = useCallback(async () => {
    const { data: sum } = await api.get(`/api/projects/${id}/summary`);
    setSummary(sum);

    const jobs = [api.get('/api/categories').then((r) => setCategories(r.data))];
    if (sum.project.access !== 'worker') {
      jobs.push(api.get(`/api/projects/${id}/expenses`).then((r) => setExpenses(r.data)));
    }
    if (sum.project.access !== 'client') {
      jobs.push(api.get(`/api/projects/${id}/workers`).then((r) => setWorkers(r.data)));
    }
    await Promise.all(jobs);
    setLoading(false);
  }, [id]);

  useEffect(() => {
    load().catch(() => setLoading(false));
  }, [load]);

  if (loading || !summary) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const project = summary.project;

  const tabs =
    access === 'owner'
      ? [
          { label: t('project.overview'), node: <OverviewTab summary={summary} /> },
          {
            label: t('project.expenses'),
            node: (
              <ExpensesTab projectId={id} expenses={expenses} categories={categories} access={access} onChanged={load} />
            ),
          },
          {
            label: t('project.workers'),
            node: <WorkersTab projectId={id} workers={workers} access={access} onChanged={load} />,
          },
        ]
      : access === 'client'
        ? [
            { label: t('project.overview'), node: <OverviewTab summary={summary} /> },
            {
              label: t('project.expenses'),
              node: (
                <ExpensesTab projectId={id} expenses={expenses} categories={categories} access={access} onChanged={load} />
              ),
            },
          ]
        : [
            {
              label: t('project.workers'),
              node: <WorkersTab projectId={id} workers={workers} access={access} onChanged={load} />,
            },
          ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <IconButton onClick={() => navigate('/')} edge="start">
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography variant="h6" noWrap>
            {project.name}
          </Typography>
          {project.site_address && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
            >
              <PlaceIcon sx={{ fontSize: 13 }} /> {project.site_address}
            </Typography>
          )}
        </Box>
        <Chip
          size="small"
          label={project.status === 'active' ? t('project.active') : t('project.completed')}
          color={project.status === 'active' ? 'success' : 'default'}
          variant="outlined"
        />
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="fullWidth">
        {tabs.map((tabDef, i) => (
          <Tab key={i} label={tabDef.label} />
        ))}
      </Tabs>

      {tabs[Math.min(tab, tabs.length - 1)].node}
    </Box>
  );
};

export default ProjectDetail;
